<script setup lang="ts">
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import VButton from './v-button.vue'

const props = withDefaults(
  defineProps<{
    modalStore: { state: { active: boolean }; close: () => void }
    title: string
    message: string
    confirmLabel?: string
    cancelLabel?: string
    variant?: 'primary' | 'danger'
    loading?: boolean
  }>(),
  {
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    variant: 'primary',
    loading: false,
  },
)

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

const buttonVariant = {
  primary: 'primary',
  danger: 'v-red',
} as const

const onConfirm = () => {
  emit('confirm')
}

const onCancel = () => {
  emit('cancel')
  props.modalStore.close()
}
</script>

<template>
  <TransitionRoot appear :show="modalStore.state.active" as="template">
    <Dialog as="div" role="alertdialog" @close="onCancel" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-white bg-opacity-25 bg-gradient-to-tr from-[#52B7B9]/0 via-[#52B7B9]/10 to-[#5D8DB4]/70 backdrop-blur"
        ></div>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform rounded-lg bg-white px-6 pb-6 pt-6 shadow-xl ring-1 ring-black ring-opacity-5 transition-all"
            >
              <DialogTitle class="text-lg font-semibold text-gray-800">{{ title }}</DialogTitle>
              <p class="mt-3 text-sm leading-relaxed text-gray-600">{{ message }}</p>
              <div class="mt-6 flex justify-end gap-3">
                <VButton :disabled="loading" @click="onCancel">{{ cancelLabel }}</VButton>
                <VButton :variant="buttonVariant[variant]" :loading="loading" @click="onConfirm">
                  {{ confirmLabel }}
                </VButton>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
